import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { Expand, EyeOff, X } from 'lucide-react'
import { useMapsAppCheckReady } from '@/components/maps/MapsAppCheckBridge'
import { Spinner } from '@/components/ui/spinner'
import { attachSharedStreetViewPanorama } from '@/lib/street-view-panorama-pool'
import {
  calculateBearing,
  fetchStreetViewNearProperty,
  StreetViewMetadataError,
} from '@/lib/street-view'
import { cn } from '@/lib/utils'

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined

type StreetViewTarget = {
  panoId: string
  heading: number
}

/**
 * Inline Street View for the property profile — same heading approach as iOS
 * `StreetView`. Uses the shell `MapsProvider` and a shared panorama instance so
 * rapid property opens do not exhaust WebGL contexts (which blanks the map).
 */
export function StreetViewPanel({
  latitude,
  longitude,
}: {
  latitude: number
  longitude: number
}) {
  if (!MAPS_KEY) return null
  return <StreetViewPanelInner latitude={latitude} longitude={longitude} />
}

function StreetViewPanelInner({
  latitude,
  longitude,
}: {
  latitude: number
  longitude: number
}) {
  const appCheckReady = useMapsAppCheckReady()
  const streetViewLib = useMapsLibrary('streetView')
  const [target, setTarget] = useState<StreetViewTarget | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [fullscreen, setFullscreen] = useState(false)

  useEffect(() => {
    if (!appCheckReady || !streetViewLib) return

    let cancelled = false
    setLoading(true)
    setError(null)
    setTarget(null)
    setFullscreen(false)

    const property = { lat: latitude, lng: longitude }

    ;(async () => {
      try {
        const service = new streetViewLib.StreetViewService()
        const metadata = await fetchStreetViewNearProperty(service, property)
        if (cancelled) return
        const heading = calculateBearing(
          { lat: metadata.panoLat, lng: metadata.panoLng },
          property,
        )
        setTarget({ panoId: metadata.panoId, heading })
      } catch (e) {
        if (cancelled) return
        if (e instanceof StreetViewMetadataError && e.code === 'no_street_view') {
          setError('Street View isn’t available for this property.')
        } else {
          console.error('[street-view] failed to resolve panorama', e)
          setError('Couldn’t load Street View.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [appCheckReady, streetViewLib, latitude, longitude])

  useEffect(() => {
    if (!fullscreen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false)
    }
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKey)
    }
  }, [fullscreen])

  const showCanvas = Boolean(target && streetViewLib && !loading && !error)

  const fullscreenOverlay =
    fullscreen && showCanvas && target && streetViewLib
      ? createPortal(
          <div
            className="fixed inset-0 z-[100] flex flex-col bg-black"
            role="dialog"
            aria-modal="true"
            aria-label="Street View fullscreen"
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-end p-3 pt-[max(0.75rem,env(safe-area-inset-top))] pr-[max(0.75rem,env(safe-area-inset-right))]">
              <button
                type="button"
                onClick={() => setFullscreen(false)}
                className="pointer-events-auto inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-black/80"
                aria-label="Close fullscreen Street View"
              >
                <X className="h-5 w-5" strokeWidth={2.5} />
              </button>
            </div>
            <StreetViewCanvas
              streetViewLib={streetViewLib}
              panoId={target.panoId}
              heading={target.heading}
              className="h-full w-full flex-1"
            />
          </div>,
          document.body,
        )
      : null

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl border border-border bg-surface max-md:rounded-tr-none">
        {loading || !appCheckReady || !streetViewLib ? (
          <div className="flex h-[220px] items-center justify-center sm:h-[250px]">
            <Spinner />
          </div>
        ) : error || !target ? (
          <div className="flex h-[220px] flex-col items-center justify-center gap-2 px-4 text-center sm:h-[250px]">
            <EyeOff className="h-8 w-8 text-muted" strokeWidth={1.5} />
            <p className="text-sm text-muted">{error ?? 'Street View isn’t available.'}</p>
          </div>
        ) : (
          <>
            {/* Hide inline host while fullscreen so both don’t fight over the shared panorama. */}
            {!fullscreen ? (
              <StreetViewCanvas
                streetViewLib={streetViewLib}
                panoId={target.panoId}
                heading={target.heading}
                className="h-[220px] w-full sm:h-[250px]"
              />
            ) : (
              <div className="h-[220px] w-full bg-black sm:h-[250px]" />
            )}
            {!fullscreen ? (
              <button
                type="button"
                onClick={() => setFullscreen(true)}
                className="street-view-expand-chip"
                aria-label="Open Street View fullscreen"
              >
                <Expand className="h-3.5 w-3.5 translate-x-px -translate-y-px md:h-4 md:w-4 md:translate-x-0 md:translate-y-0" />
              </button>
            ) : null}
          </>
        )}
      </div>
      {fullscreenOverlay}
    </>
  )
}

function StreetViewCanvas({
  streetViewLib,
  panoId,
  heading,
  className,
}: {
  streetViewLib: google.maps.StreetViewLibrary
  panoId: string
  heading: number
  className?: string
}) {
  const hostRef = useRef<HTMLDivElement>(null)
  const [canvasError, setCanvasError] = useState(false)

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    try {
      return attachSharedStreetViewPanorama(host, streetViewLib, {
        panoId,
        heading,
      })
    } catch (e) {
      console.error('[street-view] failed to attach panorama', e)
      setCanvasError(true)
    }
  }, [streetViewLib, panoId, heading])

  if (canvasError) {
    return (
      <div
        className={cn(
          'flex items-center justify-center bg-surface px-4 text-center text-sm text-muted',
          className,
        )}
      >
        Couldn’t load Street View.
      </div>
    )
  }

  return <div ref={hostRef} className={cn('bg-surface', className)} />
}
